/**
 * Compact product deal card — Apple-inspired minimal snapshot with expandable details.
 */
import React, { useMemo, useState } from 'react';
import { CaretDown, CaretUp, PencilSimple, Sparkle, WarningCircle, X } from '@phosphor-icons/react';
import type { PreContractProduct, PreContractIntakeForm } from '../lib/preContractTypes';
import type { SolarDesignRevision } from '../lib/preContractSolarDesign';
import type { PreContractSummaryItem } from './PreContractModuleStep.types';
import { useOneButtonStore } from '../../store/oneButtonStore';
import {
    buildPreContractRoofDesignSnapshot,
    formatRoofPricePerSqft,
    formatRoofProductLine,
    type PreContractRoofDesignSnapshot,
} from '../lib/preContractRoofDesign';
import {
    buildPreContractLivePpwQuotes,
    formatEscalatorPercent,
    pickRecommendedPartnerQuote,
    type PreContractPpwMetricsOverride,
} from '../lib/preContractPpwPricing';

export type PreContractProductDealCardProps = {
    product: PreContractProduct;
    form: PreContractIntakeForm;
    icon: React.ReactNode;
    accent: string;
    label: string;
    metricsOverride?: PreContractPpwMetricsOverride | null;
    summaryItems?: PreContractSummaryItem[];
    onOpenDesign: () => void;
    onRemove: () => void;
    onOpenPricingDetails?: () => void;
    onGenerateQuote?: () => void;
    generateQuoteDisabled?: boolean;
    generateQuoteDisabledReason?: string;
    /** Energy community sites should recommend Enfin without hiding other partners. */
    preferEnfinPartner?: boolean;
    /** Solar layout was applied from AI or edited manually since the saved baseline. */
    designRevision?: SolarDesignRevision | null;
    designModified?: boolean;
    appliedDesignLabel?: string | null;
};

function formatMoney(value: number | null | undefined, digits = 0): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: digits,
        minimumFractionDigits: digits,
    });
}

function formatPpw(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return `$${value.toFixed(2)}/W`;
}

function displayProductName(product: PreContractProduct): string {
    if (product === 'RRR') return 'R&R';
    if (product === 'RR') return 'Remove & reinstall';
    return product;
}

function SolarDetailsPanel({
    form,
    metricsOverride,
    summaryItems,
    onOpenPricingDetails,
    onGenerateQuote,
    generateQuoteDisabled,
    generateQuoteDisabledReason,
    preferEnfinPartner = false,
}: {
    form: PreContractIntakeForm;
    metricsOverride?: PreContractPpwMetricsOverride | null;
    summaryItems: PreContractSummaryItem[];
    onOpenPricingDetails?: () => void;
    onGenerateQuote?: () => void;
    generateQuoteDisabled?: boolean;
    generateQuoteDisabledReason?: string;
    preferEnfinPartner?: boolean;
}) {
    const result = useMemo(
        () => buildPreContractLivePpwQuotes(form, 0.0299, null, metricsOverride),
        [form, metricsOverride],
    );
    const bestQuote = useMemo(
        () => pickRecommendedPartnerQuote(result.quotes, { preferEnfin: preferEnfinPartner }),
        [result.quotes, preferEnfinPartner],
    );
    const metricByLabel = (label: string) =>
        summaryItems.find((item) => item.label === label)?.value ?? '—';
    const productionDisplay =
        result.annualProductionKwh > 0
            ? `${Math.round(result.annualProductionKwh).toLocaleString()} kWh`
            : metricByLabel('System Production');

    return (
        <div className="pci-product-deal-card__details-inner">
            <dl className="pci-product-deal-card__detail-grid">
                <div>
                    <dt>PPW</dt>
                    <dd>{formatPpw(bestQuote?.ppw)}</dd>
                </div>
                <div>
                    <dt>Multiplier</dt>
                    <dd>{result.multiplier > 0 ? result.multiplier.toFixed(3) : '—'}</dd>
                </div>
                <div>
                    <dt>Production</dt>
                    <dd>{productionDisplay}</dd>
                </div>
                <div>
                    <dt>Usage</dt>
                    <dd>{metricByLabel('Utility Usage')}</dd>
                </div>
                <div>
                    <dt>APR</dt>
                    <dd>{formatEscalatorPercent(result.escalator)}</dd>
                </div>
                <div>
                    <dt>Term</dt>
                    <dd>180 mo</dd>
                </div>
            </dl>
            {bestQuote && !bestQuote.available && bestQuote.reason ? (
                <p className="pci-product-deal-card__details-warn">
                    <WarningCircle size={12} weight="fill" aria-hidden />
                    {bestQuote.reason}
                </p>
            ) : null}
            <div className="pci-product-deal-card__details-actions">
                {onGenerateQuote ? (
                    <button
                        type="button"
                        className="pci-product-deal-card__details-btn pci-product-deal-card__details-btn--primary"
                        disabled={generateQuoteDisabled}
                        title={
                            generateQuoteDisabled
                                ? generateQuoteDisabledReason
                                : 'Generate quote'
                        }
                        onClick={(event) => {
                            event.stopPropagation();
                            onGenerateQuote();
                        }}
                    >
                        Generate quote
                    </button>
                ) : null}
                {onOpenPricingDetails ? (
                    <button
                        type="button"
                        className="pci-product-deal-card__details-btn"
                        onClick={(event) => {
                            event.stopPropagation();
                            onOpenPricingDetails();
                        }}
                    >
                        Full pricing
                    </button>
                ) : null}
            </div>
        </div>
    );
}

function RoofCardSummary({ snapshot }: { snapshot: PreContractRoofDesignSnapshot }) {
    const priceLabel = formatRoofPricePerSqft(snapshot.pricePerSqft);

    return (
        <div className="pci-product-deal-card__roof-summary">
            {snapshot.shingleColor ? (
                <p className="pci-product-deal-card__roof-color">{snapshot.shingleColor}</p>
            ) : null}
            <div className="pci-product-deal-card__roof-tags" aria-label="Roof pricing and scope">
                {priceLabel ? (
                    <span className="pci-product-deal-card__roof-tag pci-product-deal-card__roof-tag--price">
                        {priceLabel}
                    </span>
                ) : null}
                {snapshot.low > 0 ? (
                    <span className="pci-product-deal-card__roof-tag">
                        {snapshot.low} low sq
                    </span>
                ) : null}
                {snapshot.normal > 0 ? (
                    <span className="pci-product-deal-card__roof-tag">
                        {snapshot.normal} normal sq
                    </span>
                ) : null}
            </div>
        </div>
    );
}

function RoofDetailsPanel({ snapshot }: { snapshot: PreContractRoofDesignSnapshot }) {
    return (
        <div className="pci-product-deal-card__details-inner">
            <dl className="pci-product-deal-card__detail-grid">
                <div>
                    <dt>Product line</dt>
                    <dd>{formatRoofProductLine(snapshot) ?? '—'}</dd>
                </div>
                <div>
                    <dt>Shingle color</dt>
                    <dd>{snapshot.shingleColor ?? '—'}</dd>
                </div>
                <div>
                    <dt>Price / sq ft</dt>
                    <dd>{formatRoofPricePerSqft(snapshot.pricePerSqft) ?? '—'}</dd>
                </div>
                <div>
                    <dt>Low slope</dt>
                    <dd>{snapshot.low > 0 ? `${snapshot.low} sq` : '—'}</dd>
                </div>
                <div>
                    <dt>Normal slope</dt>
                    <dd>{snapshot.normal > 0 ? `${snapshot.normal} sq` : '—'}</dd>
                </div>
                <div>
                    <dt>Total scope</dt>
                    <dd>{snapshot.total > 0 ? `${snapshot.total} sq` : '—'}</dd>
                </div>
                {snapshot.minSolarRate != null && snapshot.proposedSolarRate != null ? (
                    <div className="pci-product-deal-card__detail-span">
                        <dt>Solar rates</dt>
                        <dd>
                            {`Min ${snapshot.minSolarRate.toFixed(3)} · Proposed ${snapshot.proposedSolarRate.toFixed(3)}`}
                        </dd>
                    </div>
                ) : null}
            </dl>
        </div>
    );
}

function BatteryDetailsPanel({ form }: { form: PreContractIntakeForm }) {
    const manufacturer = form.batteryManufacturer.trim() || '—';
    const name = form.batteryName.trim() || '—';
    const qty = form.batteryQuantity > 0 ? String(form.batteryQuantity) : '—';

    return (
        <div className="pci-product-deal-card__details-inner">
            <dl className="pci-product-deal-card__detail-grid">
                <div>
                    <dt>Manufacturer</dt>
                    <dd>{manufacturer}</dd>
                </div>
                <div>
                    <dt>Model</dt>
                    <dd>{name}</dd>
                </div>
                <div>
                    <dt>Quantity</dt>
                    <dd>{qty}</dd>
                </div>
            </dl>
        </div>
    );
}

export const PreContractProductDealCard: React.FC<PreContractProductDealCardProps> = ({
    product,
    form,
    icon,
    accent,
    label,
    metricsOverride = null,
    summaryItems = [],
    onOpenDesign,
    onRemove,
    onOpenPricingDetails,
    onGenerateQuote,
    generateQuoteDisabled = false,
    generateQuoteDisabledReason,
    preferEnfinPartner = false,
    designRevision = null,
    designModified = false,
    appliedDesignLabel = null,
}) => {
    const [detailsOpen, setDetailsOpen] = useState(false);
    const displayName = displayProductName(product);
    const systemDesign = useOneButtonStore((s) => s.systemDesign);
    const priceQuoteOverrides = useOneButtonStore((s) => s.priceQuoteOverrides);

    const solarSnapshot = useMemo(() => {
        if (product !== 'Solar') return null;
        const result = buildPreContractLivePpwQuotes(form, 0.0299, null, metricsOverride);
        const bestQuote = pickRecommendedPartnerQuote(result.quotes, {
            preferEnfin: preferEnfinPartner,
        });
        return { result, bestQuote };
    }, [form, metricsOverride, preferEnfinPartner, product]);

    const roofSnapshot = useMemo(() => {
        if (product !== 'Roof') return null;
        return buildPreContractRoofDesignSnapshot({
            form,
            systemDesign,
            priceQuoteOverrides,
        });
    }, [form, priceQuoteOverrides, product, systemDesign]);

    const batterySnapshot = useMemo(() => {
        if (product !== 'Battery') return null;
        return {
            manufacturer: form.batteryManufacturer.trim() || 'Battery',
            name: form.batteryName.trim() || 'Not selected',
            qty: form.batteryQuantity > 0 ? form.batteryQuantity : null,
        };
    }, [form, product]);

    const subtitle =
        product === 'Solar'
            ? solarSnapshot?.bestQuote?.partner ?? 'Best partner rate'
            : product === 'Roof'
              ? formatRoofProductLine(roofSnapshot) ?? 'Pattern & pricing'
              : product === 'Battery'
                ? batterySnapshot?.manufacturer ?? 'Energy storage'
                : displayName;

    const headline =
        product === 'Solar'
            ? formatMoney(solarSnapshot?.bestQuote?.epc, 0)
            : product === 'Roof'
              ? roofSnapshot?.configured && roofSnapshot.total > 0
                  ? `${roofSnapshot.total} squares`
                  : roofSnapshot?.configured
                    ? 'Design saved'
                    : 'Not configured'
              : product === 'Battery'
                ? batterySnapshot?.name ?? 'Not selected'
                : displayName;

    const caption =
        product === 'Solar'
            ? solarSnapshot?.bestQuote?.available
                ? [
                      formatMoney(solarSnapshot.bestQuote.estimatedMonthly, 0) + '/mo',
                      solarSnapshot.result.systemKw > 0
                          ? `${solarSnapshot.result.systemKw.toFixed(1)} kW`
                          : null,
                      solarSnapshot.bestQuote.ppw != null
                          ? formatPpw(solarSnapshot.bestQuote.ppw)
                          : null,
                  ]
                      .filter(Boolean)
                      .join(' · ')
                : solarSnapshot?.bestQuote?.reason ?? 'Pricing unavailable'
            : product === 'Roof'
              ? roofSnapshot?.configured
                  ? null
                  : 'Configure slopes and pattern'
              : product === 'Battery'
                ? batterySnapshot?.qty
                    ? `${batterySnapshot.qty} unit${batterySnapshot.qty === 1 ? '' : 's'} selected`
                    : 'Tap to configure storage'
                : 'Tap to open design';

    const handleCardActivate = (event: React.MouseEvent<HTMLElement>) => {
        const target = event.target as HTMLElement;
        if (
            target.closest(
                '.pci-product-deal-card__remove, .pci-product-deal-card__actions, .pci-product-deal-card__details',
            )
        ) {
            return;
        }
        onOpenDesign();
    };

    const revisionLabel =
        designRevision === 'ai'
            ? appliedDesignLabel
                ? `AI · ${appliedDesignLabel}`
                : 'AI design applied'
            : designRevision === 'manual'
              ? 'Manual layout'
              : designModified
                ? 'Updated layout'
                : null;

    return (
        <article
            role="listitem"
            className={`pci-product-deal-card pci-product-deal-card--${accent}${
                detailsOpen ? ' pci-product-deal-card--expanded' : ''
            }${designModified ? ' pci-product-deal-card--modified' : ''}${
                designRevision === 'ai' ? ' pci-product-deal-card--ai' : ''
            }${designRevision === 'manual' ? ' pci-product-deal-card--manual' : ''}`}
            aria-label={`${displayName} product`}
            onClick={handleCardActivate}
        >
            {designModified ? (
                <div className="pci-product-deal-card__revision" aria-label={revisionLabel ?? 'Layout updated'}>
                    {designRevision === 'ai' ? (
                        <Sparkle size={12} weight="fill" aria-hidden />
                    ) : (
                        <PencilSimple size={12} weight="bold" aria-hidden />
                    )}
                    <span>{revisionLabel}</span>
                </div>
            ) : null}
            <header className="pci-product-deal-card__header">
                <div className="pci-product-deal-card__mark">
                    <span className="pci-product-deal-card__mark-icon" aria-hidden>
                        {icon}
                    </span>
                    <span className="pci-product-deal-card__mark-label">{displayName}</span>
                </div>
                <button
                    type="button"
                    className="pci-product-deal-card__remove"
                    aria-label={`Remove ${displayName}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        onRemove();
                    }}
                >
                    <X size={11} weight="bold" />
                </button>
            </header>

            <div className="pci-product-deal-card__content">
                <p className="pci-product-deal-card__subtitle">{subtitle}</p>
                <p className="pci-product-deal-card__headline">{headline}</p>
                {product === 'Roof' && roofSnapshot?.configured ? (
                    <RoofCardSummary snapshot={roofSnapshot} />
                ) : caption ? (
                    <p className="pci-product-deal-card__caption">{caption}</p>
                ) : null}
            </div>

            <footer className="pci-product-deal-card__actions">
                <button
                    type="button"
                    className="pci-product-deal-card__action pci-product-deal-card__action--primary"
                    onClick={(event) => {
                        event.stopPropagation();
                        onOpenDesign();
                    }}
                >
                    Open design
                </button>
                <button
                    type="button"
                    className="pci-product-deal-card__action"
                    aria-expanded={detailsOpen}
                    onClick={(event) => {
                        event.stopPropagation();
                        setDetailsOpen((open) => !open);
                    }}
                >
                    {detailsOpen ? 'Hide info' : 'More info'}
                    {detailsOpen ? (
                        <CaretUp size={11} weight="bold" aria-hidden />
                    ) : (
                        <CaretDown size={11} weight="bold" aria-hidden />
                    )}
                </button>
            </footer>

            {detailsOpen ? (
                <section
                    className="pci-product-deal-card__details"
                    aria-label={`${displayName} details`}
                    onClick={(event) => event.stopPropagation()}
                >
                    {product === 'Solar' ? (
                        <SolarDetailsPanel
                            form={form}
                            metricsOverride={metricsOverride}
                            summaryItems={summaryItems}
                            preferEnfinPartner={preferEnfinPartner}
                            onOpenPricingDetails={onOpenPricingDetails}
                            onGenerateQuote={onGenerateQuote}
                            generateQuoteDisabled={generateQuoteDisabled}
                            generateQuoteDisabledReason={generateQuoteDisabledReason}
                        />
                    ) : product === 'Roof' && roofSnapshot ? (
                        <RoofDetailsPanel snapshot={roofSnapshot} />
                    ) : product === 'Battery' ? (
                        <BatteryDetailsPanel form={form} />
                    ) : (
                        <div className="pci-product-deal-card__details-inner">
                            <p className="pci-product-deal-card__details-copy">
                                Open design to configure this product.
                            </p>
                        </div>
                    )}
                </section>
            ) : null}
        </article>
    );
};

export default PreContractProductDealCard;
